import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form, ErrorMessage } from 'formik'
import { StaticQuery, graphql } from 'gatsby'
import axios from 'axios'
import { useMount } from 'react-use'
import Recaptcha from 'react-google-recaptcha'

import Button from '../UI/Button'

import styles from '../../styles/components/Contact/ContactForm.module.css'

// Form values
const FORM_INITIAL_VALUES = { fullname: '', email: '', phone: '', message: '', recaptcha: '' }

// Form error messages
const REQUIRED_FIELD_ERROR = 'Campo obrigatório'
const INVALID_EMAIL_ERROR = 'Endereço de e-mail inválido'
const RECAPTCHA_FIELD_ERROR = 'É necessário passar pela validação do reCAPTCHA'

// Email validation
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i

const ContactForm = props => {
  const initialButtonText = 'Enviar'
  const [buttonText, setButtonText] = useState(initialButtonText)
  const [isButtonDisabled, setIsButtonDisabled] = useState(false)
  const [isRecaptchaVisible, setIsRecaptchaVisible] = useState(false)
  const recaptchaRef = useRef(null)

  useMount(() => {
    setIsRecaptchaVisible(true)
  })

  const onRecaptchaVerify = (response, setFieldValue) => {
    setFieldValue('recaptcha', response)
  }

  const onSubmit = async (values, { setSubmitting, resetForm, setFieldValue }) => {
    const { data: result } = await axios.post(`${process.env.API_URL}contact/`, values, {
      headers: {
        'x-recaptcha-response': values.recaptcha
      }
    })

    setSubmitting(false)
    setIsButtonDisabled(true)

    setFieldValue('recaptcha', '')
    recaptchaRef.current.reset()

    if (result.success) {
      resetForm(FORM_INITIAL_VALUES)
      setButtonText('Mensagem enviada')
    } else {
      setButtonText('Erro ao enviar')
    }

    setTimeout(() => {
      setIsButtonDisabled(false)
      setButtonText(initialButtonText)
    }, 2000)
  }

  return (
    <Formik
      initialValues={FORM_INITIAL_VALUES}
      validate={values => {
        let errors = {}

        if (!values.fullname) {
          errors.fullname = REQUIRED_FIELD_ERROR
        }

        if (!values.email) {
          errors.email = REQUIRED_FIELD_ERROR
        } else if (!EMAIL_REGEX.test(values.email)) {
          errors.email = INVALID_EMAIL_ERROR
        }

        if (!values.phone) {
          errors.phone = REQUIRED_FIELD_ERROR
        }

        if (!values.message) {
          errors.message = REQUIRED_FIELD_ERROR
        }

        if (!values.recaptcha) {
          errors.recaptcha = RECAPTCHA_FIELD_ERROR
        }

        return errors
      }}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, handleChange, values, setFieldValue }) => (
        <Form className={styles.form}>
          <label className={styles.fieldWrapper}>
            <input
              className={styles.field}
              type='text'
              name='fullname'
              placeholder='Nome Completo'
              onChange={handleChange}
              value={values.fullname || ''}
            />
            <ErrorMessage name='fullname' component='div' className={styles.error} />
          </label>

          <label className={styles.fieldWrapper}>
            <input
              className={styles.field}
              type='email'
              name='email'
              placeholder='E-mail'
              onChange={handleChange}
              value={values.email || ''}
            />
            <ErrorMessage name='email' component='div' className={styles.error} />
          </label>

          <label className={styles.fieldWrapper}>
            <input
              className={styles.field}
              type='tel'
              name='phone'
              placeholder='Telefone'
              onChange={handleChange}
              value={values.phone || ''}
            />
            <ErrorMessage name='phone' component='div' className={styles.error} />
          </label>

          <label className={styles.fieldWrapper}>
            <textarea
              className={`${styles.field} ${styles.fieldTextarea}`}
              rows={6}
              name='message'
              placeholder='Mensagem'
              onChange={handleChange}
              value={values.message || ''}
            />
            <ErrorMessage name='message' component='div' className={styles.error} />
          </label>

          {isRecaptchaVisible && (
            <>
              <Recaptcha
                hl='pt-BR'
                ref={recaptchaRef}
                sitekey={props.site.siteMetadata.recaptchaApiKey}
                onExpired={() => setFieldValue('recaptcha', '')}
                onChange={response => onRecaptchaVerify(response, setFieldValue)}
              />
              <ErrorMessage name='recaptcha' component='div' className={styles.error} />
            </>
          )}

          <Button
            type='submit'
            isLoading={isSubmitting}
            className={styles.button}
            disabled={isButtonDisabled}
          >
            {buttonText}
          </Button>
        </Form>
      )}
    </Formik>
  )
}

ContactForm.propTypes = {
  site: PropTypes.shape({
    siteMetadata: PropTypes.shape({
      recaptchaApiKey: PropTypes.string
    })
  })
}

const contactFormQuery = graphql`
  query ContactFormQuery {
    site {
      siteMetadata {
        recaptchaApiKey
      }
    }
  }
`

export default () => (
  <StaticQuery query={contactFormQuery} render={data => <ContactForm {...data} />} />
)
